import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useCreateRecruiter } from "../../../../store/query/userAuthenticationQuery";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";

const useCreateRecruiters = () => {
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [validPhoneNumber, setValidPhoneNumber] = useState(true);
  const [onChangeValidation, setOnChangeValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createRecruiterMutation = useCreateRecruiter();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const roles = [
    { value: "admin", label: "Administrator" },
    { value: "recruiter", label: "Recruiter" },
    { value: "hiring manager", label: "Hiring Manager" },
  ];

  const handleSignUp = (values) => {
    setIsSubmitting(true);
    createRecruiterMutation.mutate(
      {
        name: values.name,
        email: values.email,
        phone: values.phone_number,
        role: values.role,
        title: values.title,
        password: values.password,
        passwordConfirm: values.confirm_password,
      },
      {
        onSuccess: () => {
          formik.resetForm();
          showToast("Created successfully");
          setIsSubmitting(false);
          navigate("/app/settings/recruiter-list");
        },
        onError: (error) => {
          setIsSubmitting(false);
          if (error.message === "Network Error") {
            showToast(error.message, "error");
          } else {
            showToast("User already exists!", "error");
          }
        },
      }
    );
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      title: "",
      role: "",
      phone_number: "",
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email().required("Email is required"),
      title: Yup.string().required("Title is required"),
      phone_number: Yup.string().required("Phone number is required"),
      role: Yup.string().required("Role is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    validateOnChange: onChangeValidation,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (validPhoneNumber) {
        handleSignUp(values);
      } else {
        showToast("Please enter a valid phone number", "error");
      }
    },
  });

  const validatePhoneNumber = (phone, country) => {
    const phoneNumber = phone.slice(country.dialCode.length);
    formik.setFieldValue(
      "phone_number",
      "+" + country.dialCode + " " + phoneNumber
    );
    if (phone.length === 0) {
      setPhoneErrorMessage("Phone number is required");
      setValidPhoneNumber(false);
      return;
    }
    const isValid = isValidPhoneNumber(
      "+" + country.dialCode + " " + phoneNumber,
      country.countryCode.toUpperCase()
    );
    if (!isValid) {
      setValidPhoneNumber(false);
      setPhoneErrorMessage("Invalid phone number");
    } else {
      setPhoneErrorMessage(null);
      setValidPhoneNumber(true);
    }
  };

  return {
    formik,
    roles,
    validatePhoneNumber,
    phoneErrorMessage,
    validPhoneNumber,
    isSubmitting,
    onChangeValidation,
    setOnChangeValidation,
  };
};

export default useCreateRecruiters;
